import { AppState } from './../../../../App.state';
import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Observable } from 'rxjs';
import { Chart, registerables } from 'chart.js';
import { QueryOptions } from 'src/app/global/domain/query.options';
import { NovoDashboardService } from 'src/app/base/services/novo-dashboard.service';
import { DialogService } from 'src/app/global/services/dialog.service';
import { CrescimentoBaseClientes, CrescimentoBaseClientesMes } from 'src/app/base/domain/dto/crescimento-base-clientes.dto';
import { CrescimentoBaseClientesKpis } from 'src/app/base/domain/dto/crescimento-base-clientes-kpis.dto';
import { CrescimentoBaseClientesCidade } from 'src/app/base/domain/dto/crescimento-base-clientes-cidade.dto';
import { CrescimentoBaseClientesDistribuicaoSegmento } from 'src/app/base/domain/dto/crescimento-base-clientes-distribuicao-segmento.dto';
import { CrescimentoBaseClientesPrimeiraOperacao } from 'src/app/base/domain/dto/crescimento-base-clientes-primeira-operacao.dto';
import { CrescimentoBaseClientesInativoBloqueado } from 'src/app/base/domain/dto/crescimento-base-clientes-inativo-bloqueado.dto';

Chart.register(...registerables);

interface LinhaDistribuicaoSegmento {
  segmento: string;
  label: string;
  porEstado: { [estado: string]: number };
  total: number;
}

// Config genérica de um drill-down paginado — getters/setters apontam pros campos de cada drill-down, reaproveitados por carregarPaginaDrillDown()/carregarMaisDrillDown().
interface PaginacaoInfinitaConfig<T> {
  buscar: (pagina: number) => Observable<T[]>;
  getPagina: () => number;
  setPagina: (pagina: number) => void;
  getFimDaLista: () => boolean;
  setFimDaLista: (fim: boolean) => void;
  getLista: () => T[];
  setLista: (lista: T[]) => void;
  getCarregandoMais: () => boolean;
  setCarregandoMais: (carregando: boolean) => void;
  setCarregado: (carregado: boolean) => void;
  getErro: () => boolean;
  setErro: (erro: boolean) => void;
  endpoint: string;
  mensagemErro: string;
}

const CORES_ESTADO: { [estado: string]: string } = {
  CE: '#42a5f5',
  BA: '#fdd835',
  PE: '#ff9800',
  PB: '#ec407a'
};
const CORES_FALLBACK = ['#ab47bc', '#ef5350', '#26a69a', '#8d6e63'];
// Tamanho de página usado por todos os drill-downs com scroll infinito (mesmo padrão em todos).
const TAMANHO_PAGINA = 50;

// Cidade sozinha não identifica unicamente (ex.: "Boa Viagem" existe em CE e em PE) —
// por isso a seleção do dropdown carrega o par cidade+estadoCidade como uma unidade só.
const OPCAO_SEM_CIDADE: CrescimentoBaseClientesCidade = { cidade: 'SEM_CIDADE', estadoCidade: '', qtdClientes: 0 };

// Definição completa de "inativo/bloqueado": só esses 4 códigos (dos 15 possíveis no sistema)
// entram nessa classificação — o back-end já filtra, aqui só formatamos o rótulo.
const LABELS_SITUACAO: { [descricaoSituacao: string]: string } = {
  INATIVO: 'Inativo',
  INATIVO_COM_PENDENCIA: 'Inativo com Pendência',
  BLOQUEADO: 'Bloqueado',
  BLOQUEADO_COM_DIVIDA: 'Bloqueado com Dívida'
};

@Component({
  selector: 'app-dashboard-crescimento-base-clientes',
  templateUrl: './dashboard-crescimento-base-clientes.component.html',
  styleUrls: ['./dashboard-crescimento-base-clientes.component.css']
})
export class DashboardCrescimentoBaseClientesComponent implements OnInit {

  @ViewChild('graficoCrescimentoBaseClientes', { static: true })
  canvas!: ElementRef<HTMLCanvasElement>;

  estado = '';
  segmento = '';
  consultor = '';
  dataInicio = '';
  dataFim = '';

  readonly opcaoSemCidade = OPCAO_SEM_CIDADE;
  cidadeSelecionada: CrescimentoBaseClientesCidade | null = null;

  segmentosDisponiveis: string[] = [];
  erroSegmentos = false;

  consultoresDisponiveis: string[] = [];
  erroConsultores = false;

  cidadesDisponiveis: CrescimentoBaseClientesCidade[] = [];
  erroCidades = false;

  lista: CrescimentoBaseClientes[] = [];
  erroCrescimento = false;
  carregado = false;

  kpis?: CrescimentoBaseClientesKpis;
  erroKpis = false;

  // Drill-down: distribuição da base por estado e segmento (tabela cruzada, lazy load)
  mostrarDistribuicaoSegmento = false;
  erroDistribuicaoSegmento = false;
  filtroDistribuicaoSegmento = '';
  estadosDistribuicaoSegmento: string[] = [];
  linhasDistribuicaoSegmento: LinhaDistribuicaoSegmento[] = [];
  private distribuicaoSegmentoCarregada = false;

  // Drill-down: clientes com primeira operação registrada (lazy load + scroll infinito, respeita os filtros da tela)
  mostrarPrimeiraOperacao = false;
  erroPrimeiraOperacao = false;
  primeiraOperacaoLista: CrescimentoBaseClientesPrimeiraOperacao[] = [];
  primeiraOperacaoCarregada = false;
  carregandoMaisPrimeiraOperacao = false;
  private primeiraOperacaoPagina = 0;
  private primeiraOperacaoFimDaLista = false;

  // Drill-down: clientes inativos e bloqueados (lazy load + scroll infinito, respeita os filtros da tela)
  mostrarInativosBloqueados = false;
  erroInativosBloqueados = false;
  inativosBloqueadosLista: CrescimentoBaseClientesInativoBloqueado[] = [];
  inativosBloqueadosCarregado = false;
  carregandoMaisInativosBloqueados = false;
  private inativosBloqueadosPagina = 0;
  private inativosBloqueadosFimDaLista = false;

  // Token de requisição — descarta respostas desatualizadas (cliques rápidos em "Atualizar")
  private requestId = 0;

  private chart?: Chart;

  constructor(
    private service: NovoDashboardService,
    private appState: AppState,
    public dialogService: DialogService
  ) { }

  ngOnInit(): void {
    this.loadDashboard();
    this.loadKpis();
    this.loadSegmentosDisponiveis();
    this.loadConsultoresDisponiveis();
    this.loadCidadesDisponiveis();
  }

  private loadSegmentosDisponiveis(): void {
    this.erroSegmentos = false;

    this.service.findCrescimentoBaseClientesSegmentos().subscribe({
      next: (response) => {
        this.segmentosDisponiveis = response ?? [];
      },
      error: (error) => {
        console.error('[DashboardCrescimentoBaseClientes] Falha em GET /novo-dashboard/crescimento-base-clientes-segmentos:', error);
        this.erroSegmentos = true;
        this.segmentosDisponiveis = [];
        this.dialogService.feedbackError(
          'Erro ao carregar a lista de segmentos para o filtro. Endpoint pode ainda não estar implementado no back-end.'
        );
      }
    });
  }

  private loadConsultoresDisponiveis(): void {
    this.erroConsultores = false;

    this.service.findCrescimentoBaseClientesConsultores().subscribe({
      next: (response) => {
        this.consultoresDisponiveis = response ?? [];
      },
      error: (error) => {
        console.error('[DashboardCrescimentoBaseClientes] Falha em GET /novo-dashboard/crescimento-base-clientes-consultores:', error);
        this.erroConsultores = true;
        this.consultoresDisponiveis = [];
        this.dialogService.feedbackError(
          'Erro ao carregar a lista de consultores para o filtro. Endpoint pode ainda não estar implementado no back-end.'
        );
      }
    });
  }

  private loadCidadesDisponiveis(): void {
    this.erroCidades = false;

    this.service.findCrescimentoBaseClientesCidades().subscribe({
      next: (response) => {
        this.cidadesDisponiveis = response ?? [];
      },
      error: (error) => {
        console.error('[DashboardCrescimentoBaseClientes] Falha em GET /novo-dashboard/crescimento-base-clientes-cidades:', error);
        this.erroCidades = true;
        this.cidadesDisponiveis = [];
        this.dialogService.feedbackError(
          'Erro ao carregar a lista de cidades para o filtro. Endpoint pode ainda não estar implementado no back-end.'
        );
      }
    });
  }

  toggleDistribuicaoSegmento(): void {
    this.mostrarDistribuicaoSegmento = !this.mostrarDistribuicaoSegmento;
    if (this.mostrarDistribuicaoSegmento && !this.distribuicaoSegmentoCarregada) {
      this.loadDistribuicaoSegmento();
    }
  }

  private loadDistribuicaoSegmento(): void {
    this.appState.isLoading = true;
    this.erroDistribuicaoSegmento = false;

    this.service.findCrescimentoBaseClientesDistribuicaoSegmento().subscribe({
      next: (response) => {
        this.buildDistribuicaoSegmentoMatriz(response ?? []);
        this.distribuicaoSegmentoCarregada = true;
        this.appState.isLoading = false;
      },
      error: (error) => {
        console.error('[DashboardCrescimentoBaseClientes] Falha em GET /novo-dashboard/crescimento-base-clientes-distribuicao-segmento:', error);
        this.erroDistribuicaoSegmento = true;
        this.estadosDistribuicaoSegmento = [];
        this.linhasDistribuicaoSegmento = [];
        this.dialogService.feedbackError(
          'Erro ao carregar a distribuição por estado e segmento. Endpoint pode ainda não estar implementado no back-end.'
        );
        this.appState.isLoading = false;
      }
    });
  }

  private buildDistribuicaoSegmentoMatriz(dados: CrescimentoBaseClientesDistribuicaoSegmento[]): void {
    this.estadosDistribuicaoSegmento = Array.from(new Set(dados.map(item => item.estado))).sort();

    const linhasPorSegmento = new Map<string, LinhaDistribuicaoSegmento>();
    dados.forEach(item => {
      let linha = linhasPorSegmento.get(item.segmento);
      if (!linha) {
        linha = {
          segmento: item.segmento,
          label: item.segmento === 'SEM_SEGMENTO' ? 'Sem segmento cadastrado' : item.segmento,
          porEstado: {},
          total: 0
        };
        linhasPorSegmento.set(item.segmento, linha);
      }
      linha.porEstado[item.estado] = (linha.porEstado[item.estado] ?? 0) + item.totalClientes;
      linha.total += item.totalClientes;
    });

    // Volume decrescente — com ~108 segmentos, os relevantes ficam sempre no topo.
    this.linhasDistribuicaoSegmento = Array.from(linhasPorSegmento.values()).sort((a, b) => b.total - a.total);
  }

  get linhasDistribuicaoSegmentoFiltradas(): LinhaDistribuicaoSegmento[] {
    const filtro = this.filtroDistribuicaoSegmento.trim().toLowerCase();
    if (!filtro) { return this.linhasDistribuicaoSegmento; }
    return this.linhasDistribuicaoSegmento.filter(linha => linha.label.toLowerCase().includes(filtro));
  }

  totalColunaDistribuicaoSegmento(estado: string): number {
    return this.linhasDistribuicaoSegmentoFiltradas.reduce((soma, linha) => soma + (linha.porEstado[estado] ?? 0), 0);
  }

  get totalGeralDistribuicaoSegmento(): number {
    return this.linhasDistribuicaoSegmentoFiltradas.reduce((soma, linha) => soma + linha.total, 0);
  }

  // --- Helper genérico compartilhado pelos drill-downs paginados com scroll infinito ---

  // reset=true: primeiro clique ou filtro mudou — zera lista/paginação e volta pra página 0.
  // reset=false: scroll infinito — concatena a nova página à lista já carregada.
  private carregarPaginaDrillDown<T>(config: PaginacaoInfinitaConfig<T>, reset: boolean): void {
    if (reset) {
      config.setPagina(0);
      config.setLista([]);
      config.setFimDaLista(false);
    }
    config.setErro(false);

    if (reset) {
      this.appState.isLoading = true;
    } else {
      config.setCarregandoMais(true);
    }

    // Captura o requestId atual — um "Atualizar" o incrementa e invalida qualquer resposta antiga em voo (reset concorrente ou página de scroll com filtro velho).
    const requestId = this.requestId;

    // Usa os filtros já aplicados na tela no momento do clique (estado/segmento/consultor/cidade/período).
    config.buscar(config.getPagina()).subscribe({
      next: (response) => {
        if (requestId !== this.requestId) { return; }
        const pagina = response ?? [];
        config.setLista(reset ? pagina : [...config.getLista(), ...pagina]);
        config.setFimDaLista(pagina.length < TAMANHO_PAGINA);
        config.setCarregado(true);
        if (reset) { this.appState.isLoading = false; } else { config.setCarregandoMais(false); }
      },
      error: (error) => {
        if (requestId !== this.requestId) { return; }
        console.error(`[DashboardCrescimentoBaseClientes] Falha em GET ${config.endpoint}:`, error);
        config.setErro(true);
        if (reset) { config.setLista([]); }
        this.dialogService.feedbackError(config.mensagemErro);
        if (reset) { this.appState.isLoading = false; } else { config.setCarregandoMais(false); }
      }
    });
  }

  private carregarMaisDrillDown<T>(config: PaginacaoInfinitaConfig<T>): void {
    if (config.getCarregandoMais() || config.getFimDaLista() || config.getErro()) { return; }
    config.setPagina(config.getPagina() + 1);
    this.carregarPaginaDrillDown(config, false);
  }

  // Chamado quando o usuário rola o container do drill-down perto do final.
  private onScrollDrillDown(event: Event, carregarMais: () => void): void {
    const elemento = event.target as HTMLElement;
    const pertoDoFim = elemento.scrollTop + elemento.clientHeight >= elemento.scrollHeight - 150;
    if (pertoDoFim) {
      carregarMais();
    }
  }

  // Barra a propagação do wheel enquanto a tabela ainda rola (senão o perfect-scrollbar do .main-panel rouba o evento); nos limites, deixa passar pra página rolar.
  private onWheelDrillDown(event: WheelEvent): void {
    const elemento = event.currentTarget as HTMLElement;
    const podeRolar = elemento.scrollHeight > elemento.clientHeight;
    if (!podeRolar) { return; }

    const indoParaBaixo = event.deltaY > 0;
    const noTopo = elemento.scrollTop <= 0;
    const noFim = elemento.scrollTop + elemento.clientHeight >= elemento.scrollHeight - 1;

    if ((indoParaBaixo && noFim) || (!indoParaBaixo && noTopo)) { return; }

    event.stopPropagation();
  }

  // --- Drill-down: clientes com primeira operação registrada ---

  toggleClientesPrimeiraOperacao(): void {
    this.mostrarPrimeiraOperacao = !this.mostrarPrimeiraOperacao;
    if (this.mostrarPrimeiraOperacao && !this.primeiraOperacaoCarregada) {
      this.carregarPaginaDrillDown(this.configPrimeiraOperacao(), true);
    }
  }

  onScrollPrimeiraOperacao(event: Event): void {
    this.onScrollDrillDown(event, () => this.carregarMaisDrillDown(this.configPrimeiraOperacao()));
  }

  onWheelPrimeiraOperacao(event: WheelEvent): void {
    this.onWheelDrillDown(event);
  }

  private configPrimeiraOperacao(): PaginacaoInfinitaConfig<CrescimentoBaseClientesPrimeiraOperacao> {
    return {
      buscar: (pagina) => this.service.findCrescimentoBaseClientesPrimeiraOperacao(this.buildQueryOptionsPaginado(pagina, true)),
      getPagina: () => this.primeiraOperacaoPagina,
      setPagina: (pagina) => { this.primeiraOperacaoPagina = pagina; },
      getFimDaLista: () => this.primeiraOperacaoFimDaLista,
      setFimDaLista: (fim) => { this.primeiraOperacaoFimDaLista = fim; },
      getLista: () => this.primeiraOperacaoLista,
      setLista: (lista) => { this.primeiraOperacaoLista = lista; },
      getCarregandoMais: () => this.carregandoMaisPrimeiraOperacao,
      setCarregandoMais: (carregando) => { this.carregandoMaisPrimeiraOperacao = carregando; },
      setCarregado: (carregado) => { this.primeiraOperacaoCarregada = carregado; },
      getErro: () => this.erroPrimeiraOperacao,
      setErro: (erro) => { this.erroPrimeiraOperacao = erro; },
      endpoint: '/novo-dashboard/crescimento-base-clientes-primeira-operacao',
      mensagemErro: 'Erro ao carregar os clientes com primeira operação. Endpoint pode ainda não estar implementado no back-end.'
    };
  }

  // --- Drill-down: clientes inativos e bloqueados ---

  toggleClientesInativosBloqueados(): void {
    this.mostrarInativosBloqueados = !this.mostrarInativosBloqueados;
    if (this.mostrarInativosBloqueados && !this.inativosBloqueadosCarregado) {
      this.carregarPaginaDrillDown(this.configInativosBloqueados(), true);
    }
  }

  onScrollInativosBloqueados(event: Event): void {
    this.onScrollDrillDown(event, () => this.carregarMaisDrillDown(this.configInativosBloqueados()));
  }

  onWheelInativosBloqueados(event: WheelEvent): void {
    this.onWheelDrillDown(event);
  }

  private configInativosBloqueados(): PaginacaoInfinitaConfig<CrescimentoBaseClientesInativoBloqueado> {
    return {
      // Não aceita filtro de consultor — só estado/segmento/cidade/período, conforme contrato do endpoint.
      buscar: (pagina) => this.service.findCrescimentoBaseClientesInativosBloqueados(this.buildQueryOptionsPaginado(pagina, false)),
      getPagina: () => this.inativosBloqueadosPagina,
      setPagina: (pagina) => { this.inativosBloqueadosPagina = pagina; },
      getFimDaLista: () => this.inativosBloqueadosFimDaLista,
      setFimDaLista: (fim) => { this.inativosBloqueadosFimDaLista = fim; },
      getLista: () => this.inativosBloqueadosLista,
      setLista: (lista) => { this.inativosBloqueadosLista = lista; },
      getCarregandoMais: () => this.carregandoMaisInativosBloqueados,
      setCarregandoMais: (carregando) => { this.carregandoMaisInativosBloqueados = carregando; },
      setCarregado: (carregado) => { this.inativosBloqueadosCarregado = carregado; },
      getErro: () => this.erroInativosBloqueados,
      setErro: (erro) => { this.erroInativosBloqueados = erro; },
      endpoint: '/novo-dashboard/crescimento-base-clientes-inativos-bloqueados',
      mensagemErro: 'Erro ao carregar os clientes inativos e bloqueados. Endpoint pode ainda não estar implementado no back-end.'
    };
  }

  formatSituacaoLabel(descricaoSituacao: string): string {
    const label = LABELS_SITUACAO[descricaoSituacao];
    if (label) { return label; }
    // Fallback defensivo para um código eventualmente não mapeado — nunca deveria vir do
    // back-end (só os 4 confirmados são retornados), mas evita quebrar a tela se acontecer.
    return descricaoSituacao
      .toLowerCase()
      .split('_')
      .map(palavra => palavra.charAt(0).toUpperCase() + palavra.slice(1))
      .join(' ');
  }

  private loadKpis(): void {
    this.erroKpis = false;

    this.service.findCrescimentoBaseClientesKpis().subscribe({
      next: (response) => {
        this.kpis = response;
      },
      error: (error) => {
        console.error('[DashboardCrescimentoBaseClientes] Falha em GET /novo-dashboard/crescimento-base-clientes-kpis:', error);
        this.erroKpis = true;
        this.kpis = undefined;
        this.dialogService.feedbackError(
          'Erro ao carregar os KPIs de crescimento da base de clientes. Endpoint pode ainda não estar implementado no back-end.'
        );
      }
    });
  }

  loadDashboard(): void {
    const requestId = ++this.requestId;

    this.appState.isLoading = true;
    this.erroCrescimento = false;
    this.carregado = false;

    // Filtro mudou → invalida cache/paginação dos drill-downs paginados abertos
    const recarregarPrimeiraOperacao = this.mostrarPrimeiraOperacao;
    this.primeiraOperacaoCarregada = false;
    const recarregarInativosBloqueados = this.mostrarInativosBloqueados;
    this.inativosBloqueadosCarregado = false;

    this.service.findAllCrescimentoBaseClientes(this.buildQueryOptions()).subscribe({
      next: (response) => {
        if (requestId !== this.requestId) { return; }
        this.lista = response ?? [];
        this.carregado = true;
        this.buildChart();
        this.appState.isLoading = false;
      },
      error: (error) => {
        if (requestId !== this.requestId) { return; }
        console.error('[DashboardCrescimentoBaseClientes] Falha em GET /novo-dashboard/crescimento-base-clientes:', error);
        this.erroCrescimento = true;
        this.lista = [];
        this.carregado = true;
        this.dialogService.feedbackError(
          'Erro ao carregar o crescimento da base de clientes. Endpoint pode ainda não estar implementado no back-end.'
        );
        this.appState.isLoading = false;
      }
    });

    if (recarregarPrimeiraOperacao) {
      this.carregarPaginaDrillDown(this.configPrimeiraOperacao(), true);
    }
    if (recarregarInativosBloqueados) {
      this.carregarPaginaDrillDown(this.configInativosBloqueados(), true);
    }
  }

  // incluirConsultor=false pro endpoint de inativos/bloqueados, que não aceita esse filtro.
  private buildFiltrosParams(incluirConsultor: boolean = true): { [key: string]: string } {
    const params: { [key: string]: string } = {};
    if (this.estado) { params.estado = this.estado; }
    if (this.segmento) { params.segmento = this.segmento; }
    if (incluirConsultor && this.consultor) { params.consultor = this.consultor; }
    if (this.cidadeSelecionada) {
      params.cidade = this.cidadeSelecionada.cidade;
      if (this.cidadeSelecionada.estadoCidade) { params.estadoCidade = this.cidadeSelecionada.estadoCidade; }
    }
    if (this.dataInicio) { params.dataInicio = this.dataInicio; }
    if (this.dataFim) { params.dataFim = this.dataFim; }
    return params;
  }

  private buildQueryOptions(): QueryOptions {
    return new QueryOptions({ params: this.buildFiltrosParams() });
  }

  private buildQueryOptionsPaginado(pagina: number, incluirConsultor: boolean): QueryOptions {
    const params = this.buildFiltrosParams(incluirConsultor);
    params.pagina = String(pagina);
    return new QueryOptions({ params });
  }

  // Tolera string ISO (formato real hoje, com timezone variável) ou objeto {year, monthValue}, por segurança caso a serialização mude.
  private parseMesChave(mes: CrescimentoBaseClientesMes): string {
    if (typeof mes === 'string') {
      return mes.substring(0, 7);
    }
    const mesNumero = mes.monthValue;
    return `${mes.year}-${String(mesNumero).padStart(2, '0')}`;
  }

  private formatMesLabel(mesChave: string): string {
    const [ano, mes] = mesChave.split('-');
    const nomesMes = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
    const indice = Number(mes) - 1;
    const nomeMes = nomesMes[indice] ?? mes;
    return `${nomeMes}/${ano}`;
  }

  private buildChart(): void {
    if (this.chart) { this.chart.destroy(); }
    if (!this.lista.length) { return; }

    const estados = Array.from(new Set(this.lista.map(item => item.estado))).sort();
    const mesesChave = Array.from(new Set(this.lista.map(item => this.parseMesChave(item.mes)))).sort();

    const totalAcumuladoPorEstado = new Map<string, Map<string, number>>();
    const ativoAcumuladoPorEstado = new Map<string, Map<string, number>>();
    const inativoBloqueadoAcumuladoPorEstado = new Map<string, Map<string, number>>();
    const novosClientesPorEstado = new Map<string, Map<string, number>>();
    estados.forEach(estado => {
      totalAcumuladoPorEstado.set(estado, new Map<string, number>());
      ativoAcumuladoPorEstado.set(estado, new Map<string, number>());
      inativoBloqueadoAcumuladoPorEstado.set(estado, new Map<string, number>());
      novosClientesPorEstado.set(estado, new Map<string, number>());
    });

    this.lista.forEach(item => {
      const mesChave = this.parseMesChave(item.mes);
      totalAcumuladoPorEstado.get(item.estado)?.set(mesChave, item.totalAcumulado);
      ativoAcumuladoPorEstado.get(item.estado)?.set(mesChave, item.totalAtivoAcumulado);
      inativoBloqueadoAcumuladoPorEstado.get(item.estado)?.set(mesChave, item.totalInativoBloqueadoAcumulado);
      novosClientesPorEstado.get(item.estado)?.set(mesChave, item.novosClientes);
    });

    const labels = mesesChave.map(mesChave => this.formatMesLabel(mesChave));

    // Meses sem linha do back-end (novosClientes=0) carregam pra frente o último acumulado conhecido, nunca caem pra 0 — vale pras 3 métricas (total, ativos, inativos/bloqueados).
    const construirSerieForwardFill = (mapaPorMes: Map<string, number> | undefined): number[] => {
      let ultimoConhecido = 0;
      return mesesChave.map(mesChave => {
        const valor = mapaPorMes?.get(mesChave);
        if (valor !== undefined) { ultimoConhecido = valor; }
        return ultimoConhecido;
      });
    };

    const datasetsPorEstado = estados.map((estado, index) => {
      const cor = CORES_ESTADO[estado] ?? CORES_FALLBACK[index % CORES_FALLBACK.length];
      return {
        label: estado,
        data: construirSerieForwardFill(totalAcumuladoPorEstado.get(estado)),
        borderColor: cor,
        backgroundColor: cor + '33',
        fill: true,
        tension: 0.3
      };
    });

    // Séries agregadas (soma de todos os estados presentes), sobrepostas às de total por estado.
    const somarPorMes = (mapas: (Map<string, number> | undefined)[]): number[] => {
      const series = mapas.map(mapa => construirSerieForwardFill(mapa));
      return mesesChave.map((_, i) => series.reduce((soma, serie) => soma + serie[i], 0));
    };

    const LABEL_ATIVOS = 'Ativos';
    const LABEL_INATIVOS_BLOQUEADOS = 'Inativos/Bloqueados';

    const datasetAtivos = {
      label: LABEL_ATIVOS,
      data: somarPorMes(estados.map(estado => ativoAcumuladoPorEstado.get(estado))),
      borderColor: '#43a047',
      backgroundColor: '#43a04733',
      fill: true,
      tension: 0.3
    };

    const datasetInativosBloqueados = {
      label: LABEL_INATIVOS_BLOQUEADOS,
      data: somarPorMes(estados.map(estado => inativoBloqueadoAcumuladoPorEstado.get(estado))),
      borderColor: '#e53935',
      backgroundColor: '#e5393533',
      fill: true,
      tension: 0.3
    };

    const datasets = [...datasetsPorEstado, datasetAtivos, datasetInativosBloqueados];

    this.chart = new Chart(this.canvas.nativeElement, {
      type: 'line',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: { y: { beginAtZero: true } },
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              label: (item) => {
                const label = item.dataset.label;
                if (!estados.includes(label)) {
                  return ` ${label}: ${item.formattedValue}`;
                }
                const mesChave = mesesChave[item.dataIndex];
                const novos = novosClientesPorEstado.get(label)?.get(mesChave) ?? 0;
                return ` ${label}: ${novos} novos este mês, total acumulado: ${item.formattedValue}`;
              }
            }
          }
        }
      }
    });
  }
}
